import { transportTimeAt, type PlaybackAnchor } from '@/utils/playbackClock';
import { getAudioNow } from '@/utils/audioNow';

/**
 * OTO-22 T8 test-only instrumentation: a ring buffer of every scheduled
 * audio attack/cancellation, plus a bridge to the audio-anchored transport
 * time and the playback store itself, exposed on `window` so Playwright
 * specs can assert on real scheduling behavior (onset timing, burst
 * detection, cancellation) instead of reverse-engineering it from pixels or
 * driving state through UI controls that don't exist yet (loop region has
 * no settings-panel control). Gated by `import.meta.env.DEV`: Vite replaces
 * this at build time, so the entire seam (and its `window` writes) is dead
 * code, eliminated from the `prod` Playwright project's real production
 * bundle -- see `frontend/playwright.config.ts`'s `prod` project.
 */
export interface SeamAttackEvent {
  kind: 'attack';
  wallClockMs: number;
  audioTime: number;
  pitch: number;
}

export interface SeamCancelEvent {
  kind: 'cancel';
  wallClockMs: number;
  audioTime: number;
  pitch: number;
}

export type SeamEvent = SeamAttackEvent | SeamCancelEvent;

export interface SeamStoreBridge {
  getState: () => { anchor: PlaybackAnchor | null };
}

interface SeamBridge {
  events: SeamEvent[];
  store: SeamStoreBridge | null;
  deriveTransportTimeNow: () => number | null;
}

declare global {
  interface Window {
    __oto22Seam?: SeamBridge;
  }
}

export const MAX_SEAM_EVENTS = 5000;

function ensureBridge(): SeamBridge | undefined {
  if (!import.meta.env.DEV) return undefined;
  if (typeof window === 'undefined') return undefined;
  if (!window.__oto22Seam) {
    window.__oto22Seam = { events: [], store: null, deriveTransportTimeNow: () => null };
  }
  return window.__oto22Seam;
}

function pushEvent(event: SeamEvent): void {
  const bridge = ensureBridge();
  if (!bridge) return;
  bridge.events.push(event);
  if (bridge.events.length > MAX_SEAM_EVENTS) bridge.events.shift();
}

/** Records a scheduled `triggerAttack` (OTO-22 M2 future-scheduled path). */
export function recordSeamAttack(pitch: number, audioTime: number): void {
  pushEvent({ kind: 'attack', wallClockMs: performance.now(), audioTime, pitch });
}

/** Records a queued attack cancelled before it fired (`cancelScheduled`). */
export function recordSeamCancel(pitch: number, audioTime: number): void {
  pushEvent({ kind: 'cancel', wallClockMs: performance.now(), audioTime, pitch });
}

/**
 * Called once from `playbackStore.ts` module scope (single owner) with the
 * store itself. Exposes the audio-anchored transport time derivation
 * (identical math to `usePlaybackEngine`'s own derivation) and the store
 * instance for scenarios with no UI control (loop region).
 */
export function installSeamStoreBridge(store: SeamStoreBridge): void {
  const bridge = ensureBridge();
  if (!bridge) return;
  bridge.store = store;
  bridge.deriveTransportTimeNow = () => {
    const anchor = store.getState().anchor;
    return anchor ? transportTimeAt(anchor, getAudioNow()) : null;
  };
}
